import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { eraseCookie, urlFromLocalOrAbsolute } from '../utils';
import type { AuthSession } from './getAuthAction';
import { AuthAction, getAuthAction } from './getAuthAction';

export { AuthAction, AuthLogMessage, type AuthSession, type GetAuthActionParams } from './getAuthAction';

const ORIGINAL_URL_STORAGE_KEY = 'talos_auth_original_url';

export interface AuthContextProviderProps {
  session?: AuthSession;
  loginUrl: string;
  logoutUrl: string;
  sessionCookieName: string;
  rememberOriginalUrl?: boolean;
}

export function AuthContextProvider({
  children,
  session: testSession,
  loginUrl,
  logoutUrl,
  sessionCookieName,
  rememberOriginalUrl,
}: PropsWithChildren<AuthContextProviderProps>) {
  const [isAuthenticated, setAuthenticated] = useState(false);

  const login = useCallback(() => {
    // DEAL-4480 -- Some clients' OAuth servers reject the `next` query parameter.
    // Store the original URL in sessionStorage instead, which works for all OAuth providers.
    if (rememberOriginalUrl) {
      sessionStorage.setItem(ORIGINAL_URL_STORAGE_KEY, window.location.href);
    }
    window.location.replace(urlFromLocalOrAbsolute(loginUrl));
  }, [loginUrl, rememberOriginalUrl]);

  const logout = useCallback(() => {
    eraseCookie(sessionCookieName);
    window.location.replace(urlFromLocalOrAbsolute(logoutUrl));
  }, [logoutUrl, sessionCookieName]);

  const canRenderChildren = useMemo(() => {
    return isAuthenticated || window.location.pathname === '/unauthorized';
  }, [isAuthenticated]);

  useEffect(() => {
    const action = getAuthAction({ sessionCookieName, testSession });

    switch (action) {
      case AuthAction.LOGIN:
        login();
        break;
      case AuthAction.LOGOUT:
        logout();
        break;
      case AuthAction.AUTHENTICATE: {
        setAuthenticated(true);
        // Redirect to the original URL if one was stored before login
        const originalUrl = sessionStorage.getItem(ORIGINAL_URL_STORAGE_KEY);
        if (originalUrl) {
          sessionStorage.removeItem(ORIGINAL_URL_STORAGE_KEY);
          // Validate origin matches to prevent open redirect attacks
          try {
            const parsedUrl = new URL(originalUrl);
            if (parsedUrl.origin !== window.location.origin) {
              break;
            }
          } catch {
            // Invalid URL, don't redirect
            break;
          }
          // Only redirect if the original URL is different from the current location
          if (originalUrl !== window.location.href) {
            window.location.replace(originalUrl);
          }
        }
        break;
      }
    }
  }, [login, logout, sessionCookieName, testSession]);

  const value = useMemo(() => ({ isAuthenticated, logout }), [isAuthenticated, logout]);

  return <AuthContext.Provider value={value}>{canRenderChildren && children}</AuthContext.Provider>;
}
